import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { DocumentRecord, InventoryItem } from "@/hooks/useDocuments";
import { useEmbarques } from "@/hooks/useEmbarques";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, X, Package, FileText, ZoomIn, ZoomOut, Download, RotateCcw, ShieldAlert, ShieldCheck, History, AlertCircle, AlertTriangle, Receipt, Info, Wine } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HsCodeSuggestionContent } from "@/components/HsCodeSuggestion";
import { cn } from "@/lib/utils";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { calcSubTotal, calcHeaderTotals } from "@/hooks/useInvoiceMath";
import { useComplianceLookup, ComplianceResult, sanitizeHsCode, formatHsCode } from "@/hooks/useComplianceLookup";
import { EmbarqueSelectField } from "@/components/EmbarqueSelectField";
import { ClienteSelectField } from "@/components/ClienteSelectField";
import { ProveedorSelectField } from "@/components/ProveedorSelectField";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { RiskRadar } from "@/components/compliance/RiskRadar";
import { AuditTrailTimeline } from "@/components/AuditTrailTimeline";
import { useAuditTrail } from "@/hooks/useAuditTrail";
import { hasInventoryItems, isExpenseType, isPackingType, normaliseTipo } from "@/lib/documentTypes";
import { ExtractedExpensesSection } from "@/components/audit/ExtractedExpensesSection";
import { useEmbarqueLockStatus } from "@/hooks/useEmbarqueLockStatus";
import { Lock } from "lucide-react";

interface Props {
  doc: DocumentRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type HeaderField = { key: string; label: string; type?: "number"; show?: "all" | "financial" | "packing" };

const HEADER_FIELDS: HeaderField[] = [
  { key: "no_documento", label: "No. Documento" },
  { key: "fecha", label: "Fecha" },
  { key: "emisor", label: "Emisor" },
  { key: "receptor", label: "Receptor" },
  // Financial-only
  { key: "moneda", label: "Moneda", show: "financial" },
  { key: "total_factura", label: "Total Factura", type: "number", show: "financial" },
  { key: "costo_flete", label: "Costo Flete", type: "number", show: "financial" },
  { key: "seguro", label: "Seguro", type: "number", show: "financial" },
  { key: "impuestos", label: "Impuestos", type: "number", show: "financial" },
  { key: "descuento_ajuste", label: "Descuento / Ajuste", type: "number", show: "financial" },
  { key: "otros_cargos", label: "Otros Cargos", type: "number", show: "financial" },
  { key: "incoterm", label: "Incoterm", show: "financial" },
  // Logistics — visible & editable for ALL document types (GIGO resolution)
  { key: "total_piezas", label: "Total Piezas", type: "number", show: "all" },
  { key: "peso_bruto_kg", label: "Peso Bruto (KG)", type: "number", show: "all" },
  { key: "peso_neto_kg", label: "Peso Neto (KG)", type: "number", show: "all" },
  { key: "volumen_cbm", label: "Volumen (CBM)", type: "number", show: "all" },
  // Common
  { key: "origen", label: "Origen" },
  { key: "destino", label: "Destino" },
  { key: "notas_tecnicas", label: "Notas Técnicas" },
  // Special: stored in datos_extra JSONB, not a direct column
  { key: "metodo_pago", label: "Método de Pago", show: "financial" },
];

function isPdf(url: string) {
  return /\.pdf(\?|#|$)/i.test(url);
}

function ViewerToolbar({ url }: { url: string }) {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  };

  return (
    <div className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-lg border border-border bg-background/90 backdrop-blur-sm p-1 shadow-md">
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Zoom In" onClick={() => zoomIn()}>
        <ZoomIn className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Zoom Out" onClick={() => zoomOut()}>
        <ZoomOut className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Resetear" onClick={() => resetTransform()}>
        <RotateCcw className="h-4 w-4" />
      </Button>
      <div className="w-px h-5 bg-border mx-0.5" />
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Descargar" onClick={handleDownload}>
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}

function DocumentViewerWithToolbar({ url }: { url: string }) {
  const { secureUrl, loading } = useSignedUrl(url);
  const sourceForType = url || "";
  const pdf = isPdf(sourceForType);
  const viewerUrl = secureUrl && pdf
    ? `https://docs.google.com/gview?url=${encodeURIComponent(secureUrl)}&embedded=true`
    : secureUrl;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
        <ShieldCheck className="h-10 w-10 animate-pulse text-primary" />
        <span className="text-sm">Autenticando documento seguro…</span>
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!secureUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        No se pudo cargar el documento.
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <TransformWrapper
        initialScale={1}
        minScale={0.25}
        maxScale={5}
        centerOnInit
        wheel={{ step: 0.1 }}
        panning={{ velocityDisabled: true }}
        doubleClick={{ mode: "reset" }}
      >
        <ViewerToolbar url={secureUrl} />
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {pdf ? (
            <iframe
              src={viewerUrl!}
              title="Documento"
              className="border border-border rounded-lg"
              style={{ width: "100%", height: "100%", pointerEvents: "auto" }}
            />
          ) : (
            <img
              src={secureUrl}
              alt="Documento"
              className="rounded-lg max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          )}
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}

function ComplianceBadge({ result }: { result: ComplianceResult }) {
  if (!result.regla) return null;
  const riesgo = (result.regla.nivel_riesgo ?? "").toLowerCase();
  const isCritico = riesgo === "crítico" || riesgo === "critico";
  const isAltoMedio = riesgo === "alto" || riesgo === "medio";
  return (
    <div className={cn(
      "flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[10px] leading-tight border mt-1",
      isCritico
        ? "bg-destructive/10 border-destructive/30 text-destructive"
        : isAltoMedio
          ? "bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400"
          : "bg-muted/50 border-border text-muted-foreground"
    )}>
      {isCritico ? <ShieldAlert className="h-3 w-3 shrink-0 mt-0.5" /> : <ShieldCheck className="h-3 w-3 shrink-0 mt-0.5" />}
      <div>
        <span className="font-semibold">{result.regla.entidad} — {result.regla.tipo_tramite}</span>
        <p className="mt-0.5 opacity-90">{result.regla.descripcion_regla}</p>
        {result.regla.nivel_riesgo && (
          <Badge className={cn(
            "mt-1 text-[9px] px-1.5 py-0",
            isCritico ? "bg-destructive text-destructive-foreground" : isAltoMedio ? "bg-orange-500 text-white" : "bg-muted text-muted-foreground"
          )}>
            {result.regla.nivel_riesgo}
          </Badge>
        )}
      </div>
    </div>
  );
}

function EditPanel({
  headerValues,
  items,
  saving,
  onSave,
  onClose,
  tipoDocumento,
  className,
  flashFields,
  flashItemIds,
  visibleHeaderFields,
  onHeaderChange,
  onItemChange,
  complianceResults,
  ivaOverrides,
  onIvaOverride,
  embarqueId,
  onEmbarqueChange,
  doc,
  fechaLlegada,
  siblingDocuments,
  onCertUploaded,
  onLinkProvider,
  selectedProveedorId,
  selectedClienteId,
  onLinkCliente,
}: {
  headerValues: Record<string, string>;
  items: (InventoryItem & { _edited?: boolean })[];
  saving: boolean;
  onSave: () => void;
  onClose: () => void;
  tipoDocumento?: string;
  className?: string;
  flashFields: Set<string>;
  flashItemIds: Set<string>;
  visibleHeaderFields: HeaderField[];
  onHeaderChange: (key: string, value: string) => void;
  onItemChange: (idx: number, field: string, value: string) => void;
  complianceResults: Record<number, ComplianceResult>;
  ivaOverrides: Record<number, number>;
  onIvaOverride: (idx: number, value: string) => void;
  embarqueId: string;
  onEmbarqueChange: (value: string) => void;
  doc: DocumentRecord;
  fechaLlegada?: string | null;
  siblingDocuments?: DocumentRecord[];
  onCertUploaded?: () => void;
  onLinkProvider: (id: string) => void;
  selectedProveedorId: string | null;
  selectedClienteId: string | null;
  onLinkCliente: (id: string) => void;
}) {
  const isPacking = isPackingType(tipoDocumento);
  const [icoExpanded, setIcoExpanded] = useState<Set<number>>(() => {
    // Auto-expand items that already have ICO data on initial mount.
    const s = new Set<number>();
    items.forEach((it, idx) => {
      const g = Number((it as any).grados_alcoholicos) || 0;
      const l = Number((it as any).litros_por_unidad) || 0;
      const t = Number((it as any).tarifa_ico) || 0;
      if (g > 0 || l > 0 || t > 0) s.add(idx);
    });
    return s;
  });
  const toggleIco = (idx: number) => setIcoExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
  });
  const { data: allEmbarques = [] } = useEmbarques();
  const embarqueClienteId = selectedClienteId
    ?? (embarqueId ? (allEmbarques.find((e: any) => e.id === embarqueId) as any)?.cliente_id ?? null : null);

  return (
    <div className={cn("flex flex-col h-full bg-card", className)}>
      {/* Sticky header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">Auditoría</h2>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Alerta global de moneda faltante — visible en todas las pestañas */}
      {(() => {
        const monedaVal = (headerValues?.["moneda"] ?? "").toString().trim();
        const monedaMissing = monedaVal.length === 0;
        return monedaMissing ? (
          <div className="px-4 pt-3 shrink-0">
            <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-2 duration-300 border-destructive/60 bg-destructive/10">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="font-semibold">Acción Requerida: Datos Financieros Incompletos</AlertTitle>
              <AlertDescription>
                No se puede procesar el costeo ni cerrar el embarque porque la moneda no fue detectada. Por favor, asigne la divisa en la sección de Identificación.
              </AlertDescription>
            </Alert>
          </div>
        ) : null;
      })()}

      {/* Tabs: condicionales por tipo de documento (taxonomía estructural) */}
      {(() => {
        const showInventorySection = hasInventoryItems(tipoDocumento);
        const showExpensesSection = isExpenseType(tipoDocumento);
        return (
      <Tabs defaultValue="datos" className="flex-1 min-h-0 flex flex-col">
        <div className="px-4 pt-2 shrink-0">
          <TabsList className="w-full">
            <TabsTrigger value="datos" className="flex-1 gap-1.5 text-xs">
              <FileText className="h-3.5 w-3.5" />
              Datos del Documento
            </TabsTrigger>
            <TabsTrigger value="historial" className="flex-1 gap-1.5 text-xs">
              <History className="h-3.5 w-3.5" />
              Historial de Cambios
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="datos" className="flex-1 min-h-0 overflow-y-auto mt-0">
          <div className="p-4 space-y-6">
            {/* Risk Radar — Analista de Riesgo Aduanero (live-merged doc) */}
            <RiskRadar
            documento={(() => {
              const mergedExtra = {
                ...((doc as any).datos_extra && typeof (doc as any).datos_extra === "object" ? (doc as any).datos_extra : {}),
                metodo_pago: headerValues["metodo_pago"] || null,
              };
              return {
                ...doc,
                incoterm: headerValues["incoterm"] ?? doc.incoterm,
                notas_tecnicas: headerValues["notas_tecnicas"] ?? doc.notas_tecnicas,
                datos_extra: mergedExtra,
              };
            })()}
            items={items}
            complianceResults={complianceResults}
            fechaLlegada={fechaLlegada}
            embarqueId={embarqueId}
            orgId={(doc as any).org_id}
            siblingDocuments={siblingDocuments}
            onCertUploaded={onCertUploaded}
            embarqueClienteId={embarqueClienteId}
          />

          {/* Block 1: Document header — grouped by domain */}
          {(() => {
            const FINANCIAL_KEYS = new Set(["moneda", "total_factura", "costo_flete", "seguro", "impuestos", "descuento_ajuste", "otros_cargos", "incoterm", "metodo_pago"]);
            const LOGISTICS_KEYS = new Set(["total_piezas", "peso_bruto_kg", "peso_neto_kg", "volumen_cbm"]);
            const identityFields = visibleHeaderFields.filter(f => !FINANCIAL_KEYS.has(f.key) && !LOGISTICS_KEYS.has(f.key));
            const financialFields = visibleHeaderFields.filter(f => FINANCIAL_KEYS.has(f.key));
            const logisticsFields = visibleHeaderFields.filter(f => LOGISTICS_KEYS.has(f.key));

            const renderField = (f: typeof visibleHeaderFields[number]) => {
              // Lock derived fields only when source-of-truth items exist:
              // - total_factura & (peso/piezas only for Packing) are auto-derived from items.
              // For Facturas/BLs/Docs Puerto → logistics inputs stay editable (GIGO resolution).
              const isPackingDerived = isPacking && items.length > 0;
              const isAutoField =
                f.key === "total_factura" ||
                (isPackingDerived && (f.key === "total_piezas" || f.key === "peso_bruto_kg"));
              const isFlashing = flashFields.has(f.key);
              const isEmisor = f.key === "emisor";
              const isReceptor = f.key === "receptor";
              const isMetodoPago = f.key === "metodo_pago";
              return (
                <div key={f.key} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Label className={cn("text-[11px] text-muted-foreground", f.key === "moneda" && !(headerValues[f.key] ?? "").toString().trim() && "text-destructive font-medium")}>{f.label}{f.key === "moneda" ? " *" : ""}</Label>
                      {f.key === "peso_bruto_kg" && !isPacking && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="inline-flex items-center text-muted-foreground/70 hover:text-foreground transition-colors" aria-label="Información del campo">
                                <Info className="h-3 w-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[260px] text-[11px] leading-snug">
                              Valor original extraído del OCR. La suma de los pesos de los ítems no afecta este campo financiero.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    {isEmisor && selectedProveedorId && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">✓ Vinculado</span>
                    )}
                    {isReceptor && selectedClienteId && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">✓ Vinculado</span>
                    )}
                  </div>
                  {f.key === "moneda" ? (
                    <>
                      <Select value={headerValues[f.key] ?? ""} onValueChange={(val) => onHeaderChange(f.key, val)}>
                        <SelectTrigger className={cn("h-8 text-xs bg-card transition-all duration-500", isFlashing && "border-primary ring-2 ring-primary/30 bg-primary/5", !(headerValues[f.key] ?? "").toString().trim() && "border-destructive ring-2 ring-destructive/30")}>
                          <SelectValue placeholder="Seleccionar moneda…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD - Dólar Estadounidense</SelectItem>
                          <SelectItem value="COP">COP - Peso Colombiano</SelectItem>
                          <SelectItem value="EUR">EUR - Euro</SelectItem>
                          <SelectItem value="CNY">CNY - Yuan Chino</SelectItem>
                        </SelectContent>
                      </Select>
                      {!(headerValues[f.key] ?? "").toString().trim() && (
                        <p className="text-[11px] text-destructive">La moneda es obligatoria para el cálculo de costos.</p>
                      )}
                    </>
                  ) : isMetodoPago ? (
                    <Select value={headerValues[f.key] ?? ""} onValueChange={(val) => onHeaderChange(f.key, val)}>
                      <SelectTrigger className={cn("h-8 text-xs bg-card transition-all duration-500", isFlashing && "border-primary ring-2 ring-primary/30 bg-primary/5")}>
                        <SelectValue placeholder="Seleccionar método" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Transferencia Bancaria">Transferencia Bancaria</SelectItem>
                        <SelectItem value="Carta de Crédito">Carta de Crédito</SelectItem>
                        <SelectItem value="Tarjeta de Crédito">Tarjeta de Crédito</SelectItem>
                        <SelectItem value="Efectivo / Cash">Efectivo / Cash</SelectItem>
                        <SelectItem value="Criptomonedas / USDT">Criptomonedas / USDT</SelectItem>
                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : isEmisor ? (
                    <div className="space-y-1">
                      <ProveedorSelectField
                        value={selectedProveedorId ?? ""}
                        onValueChange={(id) => onLinkProvider(id)}
                        placeholder="Seleccionar proveedor…"
                        defaultSearch={selectedProveedorId ? "" : (headerValues[f.key] ?? "").trim()}
                        invalid={!selectedProveedorId}
                        className={cn("h-8 text-xs", isFlashing && "border-primary ring-2 ring-primary/30 bg-primary/5")}
                      />
                      {headerValues[f.key]?.trim() && !selectedProveedorId && (
                        <p className="text-[10px] text-muted-foreground italic truncate">
                          Texto OCR: <span className="font-mono">{headerValues[f.key]}</span>
                        </p>
                      )}
                    </div>
                  ) : isReceptor ? (
                    <div className="space-y-1">
                      <ClienteSelectField
                        value={selectedClienteId ?? ""}
                        onValueChange={(id) => onLinkCliente(id)}
                        placeholder="Seleccionar cliente importador…"
                        defaultSearch={selectedClienteId ? "" : (headerValues[f.key] ?? "").trim()}
                        invalid={!selectedClienteId}
                        className={cn("h-8 text-xs", isFlashing && "border-primary ring-2 ring-primary/30 bg-primary/5")}
                      />
                      {headerValues[f.key]?.trim() && !selectedClienteId && (
                        <p className="text-[10px] text-muted-foreground italic truncate">
                          Texto OCR: <span className="font-mono">{headerValues[f.key]}</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <Input
                      className={cn("h-8 text-xs bg-card transition-all duration-500", isFlashing && "border-primary ring-2 ring-primary/30 bg-primary/5", isAutoField && "bg-muted/50")}
                      type={f.type === "number" ? "number" : "text"}
                      disabled={isAutoField}
                      value={headerValues[f.key] ?? ""}
                      title={String(headerValues[f.key] ?? "")}
                      onChange={(e) => onHeaderChange(f.key, e.target.value)}
                    />
                  )}
                </div>
              );
            };

            return (
              <div className="space-y-4">
                {/* Identity fields */}
                {identityFields.length > 0 && (
                  <div className="bg-muted/50 p-4 rounded-lg border border-border">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Identificación</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {identityFields.map(renderField)}
                    </div>
                    {/* Embarque (DO) selector */}
                    <div className="space-y-1 mt-3">
                      <Label className="text-[11px] text-muted-foreground">Embarque (DO)</Label>
                      <EmbarqueSelectField value={embarqueId} onValueChange={onEmbarqueChange} className="h-8 text-xs" />
                    </div>
                  </div>
                )}

                {/* Financial fields */}
                {financialFields.length > 0 && (
                  <div className="bg-muted/50 p-4 rounded-lg border border-border">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Datos Financieros</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {financialFields.map(renderField)}
                    </div>
                  </div>
                )}

                {/* Logistics fields */}
                {logisticsFields.length > 0 && (
                  <div className="bg-muted/50 p-4 rounded-lg border border-border">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Datos Logísticos</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {logisticsFields.map(renderField)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Block 2: Items — solo para tipos que generan inventario (Factura Internacional / Packing List) */}
          {showInventorySection && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Ítems ({items.length})
              </h3>
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                No hay ítems asociados
              </p>
            ) : (
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <Card
                    key={item.id}
                    className={cn(
                      "border-border transition-colors",
                      item._edited && "border-primary/40 bg-primary/5"
                    )}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono text-muted-foreground">
                          #{item.item_no ?? idx + 1}
                        </span>
                        {item._edited && (
                          <span className="text-[10px] text-primary font-medium">Modificado</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">SKU</Label>
                          <Input
                            className="h-7 text-xs"
                            value={item.sku ?? ""}
                            title={item.sku ?? ""}
                            onChange={(e) => onItemChange(idx, "sku", e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label
                            className={cn(
                              "text-[10px] text-muted-foreground flex items-center gap-1",
                              item.hs_code_deducido_por_ia === true && "text-amber-600 dark:text-amber-500 font-medium"
                            )}
                          >
                            HS Code
                            {item.hs_code_deducido_por_ia === true && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
                                    aria-label="Ver sugerencia de partida arancelaria"
                                  >
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 animate-pulse cursor-pointer hover:text-amber-600 transition-colors" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-0" align="start" side="bottom">
                                  <HsCodeSuggestionContent
                                    currentCode={item.hs_code || ""}
                                    compliance={complianceResults[idx]}
                                    onAccept={(code) => {
                                      onItemChange(idx, "hs_code", code);
                                      onItemChange(idx, "hs_code_deducido_por_ia", false as any);
                                    }}
                                    onClose={() => { /* radix maneja el cierre */ }}
                                  />
                                </PopoverContent>
                              </Popover>
                            )}
                          </Label>
                          <Input
                            className={cn(
                              "h-7 text-xs font-mono",
                              item.hs_code_deducido_por_ia === true && "border-amber-500 ring-1 ring-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20"
                            )}
                            value={item.hs_code || ""}
                            title={item.hs_code_deducido_por_ia === true ? "HS Code deducido por IA — valida o edita para confirmar" : undefined}
                            onChange={(e) => {
                              onItemChange(idx, "hs_code", e.target.value);
                              if (item.hs_code_deducido_por_ia === true) {
                                onItemChange(idx, "hs_code_deducido_por_ia", false as any);
                              }
                            }}
                            onBlur={() => {
                              const raw = item.hs_code || "";
                              const formatted = formatHsCode(raw);
                              if (formatted !== raw) onItemChange(idx, "hs_code", formatted);
                            }}
                          />
                          {complianceResults[idx] && (
                            <ComplianceBadge result={complianceResults[idx]} />
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">Cantidad</Label>
                          <Input
                            className="h-7 text-xs"
                            type="number"
                            value={item.cantidad ?? ""}
                            onChange={(e) => onItemChange(idx, "cantidad", e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">Unidad</Label>
                          <Input
                            className="h-7 text-xs"
                            value={item.unidad_medida ?? ""}
                            onChange={(e) => onItemChange(idx, "unidad_medida", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Descripción</Label>
                        <Input
                          className="h-7 text-xs"
                          value={item.descripcion ?? ""}
                          title={item.descripcion ?? ""}
                          onChange={(e) => onItemChange(idx, "descripcion", e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {/* Peso Bruto — always editable for ALL doc types (GIGO) */}
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">Peso Bruto (KG)</Label>
                          <Input
                            className="h-7 text-xs tabular-nums"
                            type="number"
                            step="any"
                            value={item.peso_bruto_kg ?? ""}
                            onChange={(e) => onItemChange(idx, "peso_bruto_kg", e.target.value)}
                          />
                        </div>
                        {!isPacking && (
                          <div className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground">Precio Unit.</Label>
                            <Input
                              className="h-7 text-xs tabular-nums"
                              type="number"
                              value={item.precio_unitario ?? ""}
                              onChange={(e) => onItemChange(idx, "precio_unitario", e.target.value)}
                            />
                          </div>
                        )}
                        {!isPacking && (
                          <div className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground">Subtotal</Label>
                            <Input
                              className={cn(
                                "h-7 text-xs bg-muted/50 tabular-nums transition-all duration-500",
                                flashItemIds.has(item.id) && "border-primary ring-2 ring-primary/30 bg-primary/5"
                              )}
                              type="number"
                              readOnly
                              value={item.sub_total ?? ""}
                            />
                          </div>
                        )}
                      </div>
                      {/* Compliance: Arancel (read-only) & IVA (editable override) */}
                      {!isPacking && complianceResults[idx] && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground">Arancel %</Label>
                            <Input className="h-7 text-xs bg-muted/50 font-mono" readOnly value={complianceResults[idx].gravamen_porcentaje} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground">IVA % (editable)</Label>
                            <Input
                              className="h-7 text-xs font-mono border-primary/30"
                              type="number"
                              min={0}
                              step={0.5}
                              value={ivaOverrides[idx] ?? complianceResults[idx].iva_porcentaje}
                              onChange={(e) => onIvaOverride(idx, e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                      {!isPacking && (() => {
                        const isOpen = icoExpanded.has(idx);
                        const g = Number((item as any).grados_alcoholicos) || 0;
                        const l = Number((item as any).litros_por_unidad) || 0;
                        const t = Number((item as any).tarifa_ico) || 0;
                        const icoTipo = ((item as any).ico_tipo ?? "") as string;
                        const isVehiculos = icoTipo === "VEHICULOS";
                        const hasIco = isVehiculos || g > 0 || l > 0 || t > 0;
                        // ICO YA NO se recalcula client-side. El valor autoritativo
                        // proviene del RPC calculate_landed_cost_preview tras guardar
                        // los cambios. La fórmula vive sólo en el motor SQL.
                        const icoCop = Number((item as any).icoCOP) || 0;
                        return (
                          <div className="space-y-1">
                            <button
                              type="button"
                              onClick={() => toggleIco(idx)}
                              className={cn(
                                "w-full flex items-center justify-between gap-2 text-[10px] px-2 py-1 rounded border border-dashed transition-colors",
                                hasIco ? "border-primary/40 text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted/40",
                              )}
                              aria-expanded={isOpen}
                            >
                              <span className="flex items-center gap-1.5">
                                <Wine className="h-3 w-3" />
                                ICO {hasIco ? `· ${icoCop > 0 ? `$${Math.round(icoCop).toLocaleString("es-CO")}` : "—"}` : "(opcional)"}
                                {isVehiculos && <span className="ml-1 text-[9px] uppercase opacity-70">veh.</span>}
                              </span>
                              <span className="text-[9px] uppercase tracking-wide">{isOpen ? "Ocultar" : "Editar"}</span>
                            </button>
                            {isOpen && (
                              <div className="space-y-2 pt-1">
                                <div className="space-y-1">
                                  <Label className="text-[10px] text-muted-foreground">Tipo de ICO</Label>
                                  <Select
                                    value={icoTipo || "__BEBIDAS__"}
                                    onValueChange={(v) =>
                                      onItemChange(idx, "ico_tipo" as any, v === "__BEBIDAS__" ? "" : v)
                                    }
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue placeholder="Seleccionar tipo..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__BEBIDAS__">Bebidas Alcohólicas (grados × litros)</SelectItem>
                                      <SelectItem value="VEHICULOS">Vehículos (% sobre CIF)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {isVehiculos ? (
                                  <div className="text-[10px] text-muted-foreground rounded bg-muted/40 px-2 py-1.5">
                                    ICO calculado automáticamente como % sobre el CIF (Costo + Seguro + Flete) según el rango de valor CIF.
                                    {" "}
                                    <span className="text-foreground tabular-nums">
                                      {icoCop > 0 ? `$${Math.round(icoCop).toLocaleString("es-CO")}` : "pendiente tras guardar"}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-3 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Grados (°)</Label>
                                      <Input
                                        className="h-7 text-xs tabular-nums"
                                        type="number" min={0} max={100} step={0.1}
                                        value={(item as any).grados_alcoholicos ?? ""}
                                        onChange={(e) => onItemChange(idx, "grados_alcoholicos", e.target.value)}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Litros/und</Label>
                                      <Input
                                        className="h-7 text-xs tabular-nums"
                                        type="number" min={0} step={0.01}
                                        value={(item as any).litros_por_unidad ?? ""}
                                        onChange={(e) => onItemChange(idx, "litros_por_unidad", e.target.value)}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Tarifa ICO</Label>
                                      <Input
                                        className="h-7 text-xs tabular-nums"
                                        type="number" min={0} step={1}
                                        placeholder="auto"
                                        value={(item as any).tarifa_ico ?? ""}
                                        onChange={(e) => onItemChange(idx, "tarifa_ico", e.target.value)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Nota</Label>
                        <Input
                          className="h-7 text-xs"
                          value={item.nota_item ?? ""}
                          title={item.nota_item ?? ""}
                          onChange={(e) => onItemChange(idx, "nota_item", e.target.value)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Block 3: Gastos Extraídos — solo para tipos generadores de gasto (BL, Guía Aérea, Doc Puerto, Póliza, Factura de Compras) */}
          {showExpensesSection && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="h-3.5 w-3.5 text-primary" />
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Gastos Extraídos
                </h3>
              </div>
              <ExtractedExpensesSection documentoId={doc.id} />
            </div>
          )}
        </div>
        </TabsContent>

        <TabsContent value="historial" className="flex-1 min-h-0 overflow-y-auto mt-0">
          <div className="p-4">
            <AuditTrailTimeline documentId={doc.id} />
          </div>
        </TabsContent>
      </Tabs>
        );
      })()}

      {/* Sticky footer with actions */}
      <div className="px-4 py-3 border-t border-border shrink-0 bg-card flex items-center gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving} className="flex-1 h-10 gap-2">
          <X className="h-4 w-4" />
          Cancelar
        </Button>
        <Button onClick={onSave} disabled={saving} className="flex-1 h-10 gap-2">
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Guardar Cambios
        </Button>
      </div>
    </div>
  );
}

export function AuditPanel({ doc, open, onOpenChange }: Props) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: allEmbarques = [] } = useEmbarques();
  // 🔒 Post-liquidation lock — blocks any save when the parent embarque is liquidated.
  const { isLocked: isEmbarqueLocked } = useEmbarqueLockStatus({ documentId: doc?.id });
  const { logChange } = useAuditTrail();

  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [items, setItems] = useState<(InventoryItem & { _edited?: boolean })[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ivaOverrides, setIvaOverrides] = useState<Record<number, number>>({});
  const [embarqueId, setEmbarqueId] = useState<string>("");
  const [siblingDocs, setSiblingDocs] = useState<DocumentRecord[]>([]);
  const [selectedProveedorId, setSelectedProveedorId] = useState<string | null>(null);
  const [selectedClienteId, setSelectedClienteId] = useState<string | null>(null);
  const initialClienteIdRef = useRef<string | null>(null);
  const initialProveedorIdRef = useRef<string | null>(null);

  // Lifted reactive state
  const [flashFields, setFlashFields] = useState<Set<string>>(new Set());
  const [flashItemIds, setFlashItemIds] = useState<Set<string>>(new Set());
  const isInitialLoad = useRef(true);
  const initialItemsRef = useRef<InventoryItem[]>([]);

  // Compliance engine (must be before any early return)
  const { results: complianceResults, lookup: complianceLookup, clearAll: clearCompliance } = useComplianceLookup();

  const isPacking = isPackingType(doc?.tipo_documento);

  const visibleHeaderFields = HEADER_FIELDS.filter((f) => {
    if (!f.show) return true;
    if (f.show === "packing") return isPacking;
    if (f.show === "financial") return !isPacking;
    return true;
  });

  // Load data when panel opens
  useEffect(() => {
    if (!open || !doc) return;
    // Reset state from previous document to prevent state leaks
    clearCompliance();
    setItems([]);
    setIvaOverrides({});
    isInitialLoad.current = true;
    const v: Record<string, string> = {};
    for (const f of HEADER_FIELDS) {
      if (f.key === "metodo_pago") {
        const extra = (doc as any).datos_extra;
        v[f.key] = String((extra && typeof extra === "object" && (extra as any).metodo_pago) || "");
      } else {
        v[f.key] = String((doc as any)[f.key] ?? "");
      }
    }
    setHeaderValues(v);
    const nextEmbId = (doc as any).embarque_id || "";
    setEmbarqueId(nextEmbId);
    // Eager hydration from doc itself (covers docs without embarque too)
    const initialProvIdFromDoc = (doc as any).proveedor_id || null;
    setSelectedProveedorId(initialProvIdFromDoc);
    initialProveedorIdRef.current = initialProvIdFromDoc;
    // Try eager hydration from already-cached embarques to avoid a flash of "unlinked".
    // If the embarque is not yet in cache, we DO NOT wipe — the dedicated hydration
    // effect below will fill it in once useEmbarques resolves.
    if (nextEmbId) {
      const embCached = (allEmbarques as any[]).find((e) => e.id === nextEmbId);
      if (embCached) {
        const cid = embCached.cliente_id ?? null;
        setSelectedClienteId(cid);
        initialClienteIdRef.current = cid;
        // Provider cascade: prefer embarque's provider if set (shipment-centric source of truth)
        const pid = embCached.proveedor_id ?? initialProvIdFromDoc;
        setSelectedProveedorId(pid);
        initialProveedorIdRef.current = pid;
      }
      // else: keep prior value; hydration effect will reconcile when cache arrives.
    } else {
      // No embarque assigned at all → safe to clear cliente only (prov stays from doc).
      setSelectedClienteId(null);
      initialClienteIdRef.current = null;
    }
    setLoadingItems(true);
    supabase
      .from("base_inventario")
      .select("*")
      .eq("documento_id", doc.id)
      .order("item_no", { ascending: true })
      .then(({ data, error }) => {
        setLoadingItems(false);
        if (error) {
          toast({ title: "Error cargando ítems", description: error.message, variant: "destructive" });
          return;
        }
        const loadedItems = (data ?? []) as InventoryItem[];
        initialItemsRef.current = loadedItems.map(i => ({ ...i })); // snapshot for audit
        setItems(loadedItems);
        // Pre-populate IVA overrides from DB values
        const dbOverrides: Record<number, number> = {};
        loadedItems.forEach((item, idx) => {
          if (item.iva_override != null) {
            dbOverrides[idx] = item.iva_override;
          }
        });
        setIvaOverrides(dbOverrides);
      });
  }, [open, doc]);

  // Fetch sibling documents for the same embarque (to check existing certs)
  const fetchSiblingDocs = useCallback(async () => {
    const eid = embarqueId || (doc as any)?.embarque_id;
    if (!eid) { setSiblingDocs([]); return; }
    const { data } = await supabase
      .from("registro_documentos")
      .select("*")
      .eq("embarque_id", eid);
    setSiblingDocs((data ?? []).map((d: any) => ({ ...d, embarque_numero_do: null })) as DocumentRecord[]);
  }, [embarqueId, doc]);

  useEffect(() => {
    if (open && doc) fetchSiblingDocs();
  }, [open, doc, embarqueId, fetchSiblingDocs]);

  // Hydrate selected cliente AND proveedor from the parent embarque.
  // Guard: if the embarque is not (yet) present in the cache, DO NOT overwrite with null —
  // useEmbarques may still be loading. Only clear when we're certain there is no embarqueId.
  useEffect(() => {
    if (!open || !doc) return;
    if (!embarqueId) {
      setSelectedClienteId(null);
      initialClienteIdRef.current = null;
      // Provider: keep doc-level value (no embarque to cascade from) — do NOT wipe.
      return;
    }
    const emb = (allEmbarques as any[]).find((e) => e.id === embarqueId);
    if (!emb) return; // cache not ready — keep current value, avoid red flash
    const cid = emb.cliente_id ?? null;
    setSelectedClienteId(cid);
    initialClienteIdRef.current = cid;
    // Provider cascade: embarque is source of truth when it has a proveedor_id;
    // otherwise fall back to the document's own proveedor_id (do NOT wipe to null).
    const pid = emb.proveedor_id ?? (doc as any).proveedor_id ?? null;
    setSelectedProveedorId(pid);
    initialProveedorIdRef.current = pid;
  }, [open, doc, embarqueId, allEmbarques]);

  // DERIVED TOTALS — reacts to ANY headerValues change or items change
  const costoFlete = parseFloat(headerValues["costo_flete"]) || 0;
  const seguro = parseFloat(headerValues["seguro"]) || 0;
  const impuestos = parseFloat(headerValues["impuestos"]) || 0;
  const descuentoAjuste = parseFloat(headerValues["descuento_ajuste"]) || 0;
  const otrosCargos = parseFloat(headerValues["otros_cargos"]) || 0;

  const derivedTotals = useMemo(() => {
    return calcHeaderTotals(items, costoFlete, impuestos, descuentoAjuste, seguro, otrosCargos);
  }, [items, costoFlete, seguro, impuestos, descuentoAjuste, otrosCargos]);

  // Merge derived totals into header display values.
  // total_factura is ALWAYS derived. peso_bruto_kg/total_piezas only when this is a Packing List
  // with items (source of truth). For other doc types (Factura/BL/Doc Puerto) → respect user input.
  const isPackingWithItems = isPacking && items.length > 0;
  const displayHeaderValues = useMemo(() => ({
    ...headerValues,
    total_factura: String(derivedTotals.total_factura),
    ...(isPackingWithItems
      ? {
          total_piezas: String(derivedTotals.total_piezas),
          peso_bruto_kg: String(derivedTotals.peso_bruto_kg),
        }
      : {}),
  }), [headerValues, derivedTotals, isPackingWithItems]);

  // Flash effect when derived totals change.
  // IMPORTANTE: peso_bruto_kg y total_piezas SOLO destellan cuando el documento es Packing+items
  // (única situación donde se persisten al UPDATE). Para Facturas/BLs/Doc Puerto, editar el peso
  // de un ítem NO debe parpadear la cabecera — el OCR/manual del header es la fuente de verdad.
  const prevTotalsRef = useRef(derivedTotals);
  useEffect(() => {
    if (isInitialLoad.current) return;
    const prev = prevTotalsRef.current;
    const changed: string[] = [];
    if (prev.total_factura !== derivedTotals.total_factura) changed.push("total_factura");
    if (isPackingWithItems) {
      if (prev.total_piezas !== derivedTotals.total_piezas) changed.push("total_piezas");
      if (prev.peso_bruto_kg !== derivedTotals.peso_bruto_kg) changed.push("peso_bruto_kg");
    }
    prevTotalsRef.current = derivedTotals;
    if (changed.length > 0) {
      setFlashFields(new Set(changed));
      setTimeout(() => setFlashFields(new Set()), 1200);
    }
  }, [derivedTotals, isPackingWithItems]);

  // Handle header field changes — simple state update, totals derived automatically
  const handleHeaderChange = useCallback((key: string, value: string) => {
    isInitialLoad.current = false;
    setHeaderValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle item field changes with cascading sub_total calc
  const handleItemChange = useCallback((idx: number, field: string, value: string) => {
    isInitialLoad.current = false;
    setItems((prev) => {
      const copy = [...prev];
      const updated = { ...copy[idx], [field]: value, _edited: true };

      // Cascading: auto-calc sub_total when cantidad or precio_unitario change
      if (field === "cantidad" || field === "precio_unitario") {
        const cant = field === "cantidad" ? value : String(updated.cantidad ?? "");
        const precio = field === "precio_unitario" ? value : String(updated.precio_unitario ?? "");
        updated.sub_total = calcSubTotal(cant, precio) as any;

        // Flash the item's sub_total
        const itemId = updated.id;
        setFlashItemIds((prev) => new Set(prev).add(itemId));
        setTimeout(() => setFlashItemIds((prev) => {
          const n = new Set(prev);
          n.delete(itemId);
          return n;
        }), 1200);
      }

      copy[idx] = updated;
      return copy;
    });
  }, []);

  // IVA override handler
  const handleIvaOverride = useCallback((idx: number, value: string) => {
    const parsed = parseFloat(value);
    setIvaOverrides((prev) => ({ ...prev, [idx]: isNaN(parsed) ? 0 : parsed }));
  }, []);

  // Auto-populate IVA overrides from compliance results (only when new results arrive)
  const complianceKeysRef = useRef("");
  useEffect(() => {
    const newKey = Object.keys(complianceResults).sort().map(k => `${k}:${complianceResults[Number(k)].iva_porcentaje}`).join("|");
    if (newKey === complianceKeysRef.current) return;
    complianceKeysRef.current = newKey;
    const newOverrides: Record<number, number> = {};
    for (const [k, v] of Object.entries(complianceResults)) {
      const idx = Number(k);
      // Only set if user hasn't already overridden
      if (!(idx in ivaOverrides)) {
        newOverrides[idx] = v.iva_porcentaje;
      }
    }
    if (Object.keys(newOverrides).length > 0) {
      setIvaOverrides((prev) => ({ ...newOverrides, ...prev }));
    }
  }, [complianceResults]);

  const handleSave = async () => {
    if (!doc) return;
    if (isEmbarqueLocked) {
      toast({
        title: "Embarque liquidado",
        description: "No se pueden guardar cambios. El sello criptográfico bloquea modificaciones. Reabrir requiere autorización del OWNER.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);

    // Helper: parse numeric input preserving null when blank (vs forcing 0)
    const parseNumOrNull = (raw: any): number | null => {
      const t = String(raw ?? "").trim();
      if (t === "") return null;
      const n = parseFloat(t);
      return Number.isFinite(n) ? n : null;
    };

    // Logistics fields are user-editable for ALL doc types — preserve null/empty semantics
    const LOGISTICS_NULLABLE = new Set(["peso_bruto_kg", "peso_neto_kg", "volumen_cbm", "total_piezas"]);

    const headerUpdate: Record<string, any> = {};
    for (const f of HEADER_FIELDS) {
      // Skip derived/special fields — handled below
      if (["total_factura", "total_piezas", "peso_bruto_kg", "metodo_pago"].includes(f.key)) continue;
      if (LOGISTICS_NULLABLE.has(f.key)) {
        headerUpdate[f.key] = parseNumOrNull(headerValues[f.key]);
      } else if (f.type === "number") {
        headerUpdate[f.key] = parseFloat(headerValues[f.key]) || 0;
      } else {
        headerUpdate[f.key] = headerValues[f.key] || null;
      }
    }
    // Total factura: always derived from items
    headerUpdate["total_factura"] = derivedTotals.total_factura;
    // Logistics derivation: only OVERRIDE with derived values when this is a Packing List WITH items.
    // For Facturas, BLs, Docs de Puerto → respect user/OCR input (allow null).
    if (isPacking && items.length > 0) {
      headerUpdate["total_piezas"] = derivedTotals.total_piezas;
      headerUpdate["peso_bruto_kg"] = derivedTotals.peso_bruto_kg;
    } else {
      headerUpdate["total_piezas"] = parseNumOrNull(headerValues["total_piezas"]);
      headerUpdate["peso_bruto_kg"] = parseNumOrNull(headerValues["peso_bruto_kg"]);
    }
    headerUpdate["embarque_id"] = embarqueId || null;
    headerUpdate["proveedor_id"] = selectedProveedorId;
    // Merge metodo_pago into datos_extra JSONB preserving existing keys
    const existingExtra = (doc as any).datos_extra && typeof (doc as any).datos_extra === "object"
      ? { ...(doc as any).datos_extra }
      : {};
    existingExtra.metodo_pago = headerValues["metodo_pago"] || null;
    headerUpdate["datos_extra"] = existingExtra;
    const { error: headerErr } = await supabase
      .from("registro_documentos")
      .update(headerUpdate)
      .eq("id", doc.id);

    if (headerErr) {
      toast({ title: "Error guardando cabecera", description: headerErr.message, variant: "destructive" });
      setSaving(false);
      return;
    }

    // Audit trail: log header field changes (compare against original doc snapshot)
    for (const f of HEADER_FIELDS) {
      if (["total_factura", "total_piezas", "peso_bruto_kg"].includes(f.key)) continue;
      const oldVal = String((doc as any)[f.key] ?? "").trim();
      const newVal = (headerValues[f.key] ?? "").trim();
      if (oldVal !== newVal) {
        await logChange(doc.id, f.key, oldVal, newVal);
      }
    }

    // Audit trail: log item-level changes (compare against initial snapshot)
    const AUDIT_ITEM_FIELDS = ["hs_code", "cantidad", "precio_unitario", "peso_bruto_kg", "sku", "descripcion", "iva_override"] as const;
    for (const item of items) {
      const original = initialItemsRef.current.find(o => o.id === item.id);
      if (!original) continue;
      for (const field of AUDIT_ITEM_FIELDS) {
        const oldVal = String((original as any)[field] ?? "").trim();
        const newVal = String((item as any)[field] ?? "").trim();
        if (oldVal !== newVal) {
          await logChange(doc.id, field, oldVal, newVal);
        }
      }
    }

    // Cascade: sync proveedor_id to the parent embarque + learn OCR alias
    if (embarqueId && selectedProveedorId) {
      const { error: embarqueErr } = await supabase
        .from("embarques")
        .update({ proveedor_id: selectedProveedorId })
        .eq("id", embarqueId);
      if (embarqueErr) {
        console.error("Error syncing proveedor to embarque:", embarqueErr.message);
      }
    }
    // Persist proveedor_id on the document itself + learn OCR alias when changed
    if (selectedProveedorId && selectedProveedorId !== initialProveedorIdRef.current) {
      const { error: docProvErr } = await supabase
        .from("registro_documentos")
        .update({ proveedor_id: selectedProveedorId })
        .eq("id", doc.id);
      if (docProvErr) {
        console.error("Error syncing proveedor to document:", docProvErr.message);
      }
      const emisorOcr = (headerValues["emisor"] ?? "").trim();
      if (emisorOcr) {
        await supabase
          .from("proveedor_aliases")
          .insert({
            proveedor_id: selectedProveedorId,
            nombre_ocr: emisorOcr,
            org_id: (doc as any).org_id,
          })
          .then(({ error }) => {
            if (error && !/duplicate|unique/i.test(error.message)) {
              console.warn("Proveedor alias learn failed:", error.message);
            }
          });
      }
      initialProveedorIdRef.current = selectedProveedorId;
    }

    // Cascade: propagate cliente_id to the parent embarque (the document's owner == embarque's owner)
    if (embarqueId && selectedClienteId && selectedClienteId !== initialClienteIdRef.current) {
      const { error: cliErr } = await supabase
        .from("embarques")
        .update({ cliente_id: selectedClienteId })
        .eq("id", embarqueId);
      if (cliErr) {
        console.error("Error syncing cliente to embarque:", cliErr.message);
        toast({ title: "Aviso", description: "No se pudo propagar el cliente al embarque.", variant: "destructive" });
      } else {
        // Learn alias: store the OCR receptor text so the AI auto-matches next time.
        const receptorOcr = (headerValues["receptor"] ?? "").trim();
        if (receptorOcr) {
          await supabase
            .from("cliente_aliases")
            .insert({
              cliente_id: selectedClienteId,
              nombre_ocr: receptorOcr,
              org_id: (doc as any).org_id,
            })
            .then(({ error }) => {
              // Ignore unique-constraint violations (alias already learned).
              if (error && !/duplicate|unique/i.test(error.message)) {
                console.warn("Cliente alias learn failed:", error.message);
              }
            });
        }
        initialClienteIdRef.current = selectedClienteId;
      }
    }

    // Save ALL items that were edited OR have IVA overrides
    const itemsToSave = items.filter((i, idx) => i._edited || idx in ivaOverrides);
    for (const item of itemsToSave) {
      const idx = items.indexOf(item);
      const updatePayload: Record<string, any> = {
        sku: item.sku || null,
        hs_code: (item as any).hs_code || null,
        // Persist HITL verification: if user edited/verified, flag becomes false
        hs_code_deducido_por_ia: (item as any).hs_code_deducido_por_ia === true ? true : false,
        descripcion: item.descripcion || null,
        cantidad: parseFloat(String(item.cantidad)) || 0,
        peso_bruto_kg: parseFloat(String(item.peso_bruto_kg)) || 0,
        precio_unitario: parseFloat(String(item.precio_unitario)) || 0,
        sub_total: parseFloat(String(item.sub_total)) || 0,
        unidad_medida: item.unidad_medida || null,
        nota_item: item.nota_item || null,
        // ICO — multi-tipo (bebidas legacy / vehículos / cigarrillos). null si vacío.
        grados_alcoholicos: (item as any).grados_alcoholicos === "" || (item as any).grados_alcoholicos == null
          ? null
          : parseFloat(String((item as any).grados_alcoholicos)) || null,
        litros_por_unidad: (item as any).litros_por_unidad === "" || (item as any).litros_por_unidad == null
          ? null
          : parseFloat(String((item as any).litros_por_unidad)) || null,
        tarifa_ico: (item as any).tarifa_ico === "" || (item as any).tarifa_ico == null
          ? null
          : parseFloat(String((item as any).tarifa_ico)) || null,
        ico_tipo: (item as any).ico_tipo === "" || (item as any).ico_tipo == null
          ? null
          : String((item as any).ico_tipo),
      };
      // Include IVA override if set
      if (idx in ivaOverrides) {
        updatePayload.iva_override = ivaOverrides[idx];
      }
      const { error: itemErr } = await supabase
        .from("base_inventario")
        .update(updatePayload)
        .eq("id", item.id);

      if (itemErr) {
        toast({ title: `Error actualizando ítem #${item.item_no}`, description: itemErr.message, variant: "destructive" });
      }
    }

    setSaving(false);
    toast({ title: "✅ Correcciones guardadas" });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    queryClient.invalidateQueries({ queryKey: ["proveedores"] });
    onOpenChange(false);
  };

  const handleClose = () => onOpenChange(false);

  // Reactive compliance: fire lookup whenever items' hs_code values change
  const hsCodesKey = items.map(i => (i as any).hs_code ?? "").join("|");
  useEffect(() => {
    if (!items.length) return;
    items.forEach((item, idx) => {
      const code = ((item as any).hs_code ?? "").trim();
      if (code) {
        complianceLookup(idx, code);
      }
    });
  }, [hsCodesKey, complianceLookup]);

  if (!doc) return null;

  const hasFile = !!doc.archivo_url;

  const editPanelProps = {
    headerValues: displayHeaderValues,
    items,
    saving,
    onSave: handleSave,
    onClose: handleClose,
    tipoDocumento: doc?.tipo_documento ?? "",
    flashFields,
    flashItemIds,
    visibleHeaderFields,
    onHeaderChange: handleHeaderChange,
    onItemChange: handleItemChange,
    complianceResults,
    ivaOverrides,
    onIvaOverride: handleIvaOverride,
    embarqueId,
    onEmbarqueChange: setEmbarqueId,
    doc,
    fechaLlegada: allEmbarques.find(e => e.id === embarqueId)?.fecha_llegada_estimada ?? null,
    siblingDocuments: siblingDocs,
    onCertUploaded: fetchSiblingDocs,
    onLinkProvider: setSelectedProveedorId,
    selectedProveedorId,
    selectedClienteId,
    onLinkCliente: setSelectedClienteId,
  };

  // MOBILE: Full-screen dialog
  // 🔒 Banner reutilizable para modo liquidado
  const lockBanner = isEmbarqueLocked ? (
    <div className="absolute top-0 left-0 right-0 z-20 bg-amber-500/15 border-b border-amber-500/40 px-4 py-2 flex items-center gap-2 text-amber-900 dark:text-amber-200 text-xs font-medium">
      <Lock className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">
        Embarque LIQUIDADO — documento en modo solo lectura. Sello criptográfico activo.
      </span>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-full w-full h-[100dvh] p-0 gap-0 rounded-none sm:rounded-none [&>button]:hidden">
          {lockBanner}
          {loadingItems ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className={cn(isEmbarqueLocked && "pt-9 h-full")}>
              <EditPanel {...editPanelProps} />
            </div>
          )}
        <DialogTitle className="sr-only">Auditoría de Documento</DialogTitle>
      </DialogContent>
      </Dialog>
    );
  }

  // DESKTOP: Split-screen dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden [&>button]:hidden">
        <DialogTitle className="sr-only">Auditoría de Documento</DialogTitle>
        {lockBanner}
        {loadingItems ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className={cn("flex h-full overflow-hidden", isEmbarqueLocked && "pt-9")}>
            <div className={cn(
              "flex-1 min-w-0 h-full bg-muted/30 p-4 flex items-center justify-center",
            )}>
              {hasFile ? (
                <DocumentViewerWithToolbar url={doc.archivo_url!} />
              ) : (
                <div className="text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay archivo adjunto</p>
                </div>
              )}
            </div>

            <EditPanel
              {...editPanelProps}
              className="w-[420px] shrink-0 border-l border-border"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
